import Button from '@/components/global/button';
import Card from '@/components/global/card';
import CircledAvatar from '@/components/global/circled-avatar';
import Typography from '@/components/global/typography';
import Badge from '@/components/global/badge';
import { useAuth } from '@/lib/hooks/useAuth';
import { useUser } from '@/lib/hooks/useUser';
import {
  conversationsState,
  conversationsActions,
} from '@/lib/store/conversations.store';
import { getOtherParticipant } from '@/types/chat/conversations.types';
import { cn } from '@/lib/utils';
import { useI18n } from '@/locales/client';
import { observer } from '@legendapp/state/react';
import { ChevronLeft, Search, MessageCircle } from 'lucide-react';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { useEffect, useState, memo } from 'react';
import { ConversationListItem } from '@/types/chat/conversations.types';
import ROUTES from '@/lib/constants/routeConfig';

// Componente memoizado para cada conversa individual
const MemoizedConversationItem = memo(
  ({
    conversation,
    isSelected,
    onClick,
    otherParticipant,
    formatTime,
    currentUserId,
    t,
  }: {
    conversation: ConversationListItem;
    isSelected: boolean;
    onClick: () => void;
    otherParticipant: any;
    formatTime: (date: string) => string;
    currentUserId: string;
    t: any;
  }) => {
    // Verificar se há mensagem não lida (última mensagem não é do usuário atual)
    const hasUnreadMessage =
      conversation.lastMessage &&
      conversation.lastMessage.senderId !== currentUserId;
    return (
      <div className='relative'>
        <Card
          className={cn(
            'flex cursor-pointer flex-col items-start justify-center gap-1 rounded-lg border border-gray-200 bg-white p-4 transition-all duration-200 ease-in-out hover:scale-[1.02] hover:bg-gray-100 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-800',
            isSelected &&
              'border-blue-500 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20'
          )}
          style={{
            minHeight: '65.55px',
            padding: '16px',
            gap: '4px',
            borderRadius: '8px',
            boxSizing: 'border-box' as const,
          }}
          onClick={onClick}
        >
          {/* Bolinha de mensagem não lida */}
          {hasUnreadMessage && (
            <div className='absolute -right-1 -top-1 h-3 w-3 rounded-full border-2 border-white bg-red-500 dark:border-gray-900'></div>
          )}
          <div className='flex w-full items-center gap-3'>
            <CircledAvatar
              image={otherParticipant?.avatar}
              name={otherParticipant?.name || 'Unknown User'}
              size='small'
              className='!h-[25px] !max-h-[25px] !min-h-[25px] !w-[25px] !min-w-[25px] !max-w-[25px]'
            />
            <div className='flex flex-1 items-center justify-between'>
              <Typography size='p' weight='bold' color='primary'>
                {otherParticipant?.name || 'Unknown User'}
              </Typography>
              <Typography size='span' weight='normal' color='secondary'>
                {conversation.lastMessage?.createdAt
                  ? formatTime(conversation.lastMessage.createdAt)
                  : ''}
              </Typography>
            </div>
          </div>
          <div className='w-full'>
            <Typography
              size='span'
              weight='normal'
              color='secondary'
              className='line-clamp-1'
            >
              {conversation.lastMessage?.content ||
                t('dashboard.chat.noMessagesYet')}
            </Typography>
          </div>
        </Card>
      </div>
    );
  }
);

MemoizedConversationItem.displayName = 'MemoizedConversationItem';

const ChatSidebar = observer(() => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const selectedChatId = searchParams.get('chatId');
  const t = useI18n();
  const { session } = useAuth();
  const { conversations, isLoading, activeConversation } =
    conversationsState.get();
  const { isLawyer } = useUser();
  useEffect(() => {
    if (session) {
      conversationsActions.fetchConversations(session);
    }
  }, [session]);

  // Auto-select the most recent conversation if no chat is selected
  useEffect(() => {
    if (
      session &&
      conversations.length > 0 &&
      !selectedChatId &&
      !activeConversation
    ) {
      // Sort conversations by updatedAt to get the most recent one
      const sortedConversations = [...conversations].sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
      const mostRecentConversation = sortedConversations[0];
      if (mostRecentConversation) {
        handleChatSelect(mostRecentConversation.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, selectedChatId, activeConversation, session]);

  // Polling para manter conversas atualizadas
  useEffect(() => {
    if (!session) return;

    const interval = setInterval(() => {
      conversationsActions.fetchConversations(session);
    }, 30000); // Atualiza a cada 30 segundos

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const handleChatSelect = (conversationId: string) => {
    conversationsActions.setActiveConversationById(
      conversationId,
      session || undefined
    );

    // Navegar para a tela de chat completa
    if (isLawyer) {
      router.push(`${ROUTES.DASHBOARD.CONVERSATIONS}?chatId=${conversationId}`);
    } else {
      router.push(
        `${ROUTES.MARKETPLACE.CONVERSATIONS}?chatId=${conversationId}`
      );
    }
  };

  const formatTime = (dateString: string) => {
    const messageDate = new Date(dateString);
    const now = new Date();

    // Calculate total difference in minutes
    const diffInMinutes = Math.floor(
      (now.getTime() - messageDate.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) return 'Just Now';
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;

    const diffInDays = Math.floor(diffInMinutes / 1440);
    return `${diffInDays}d`;
  };

  const handleBack = () => {
    if (isLawyer) {
      router.push('/dashboard');
    } else {
      router.push('/marketplace');
    }
  };

  return (
    <div className='flex h-[calc(100vh-72px)] w-full flex-col bg-white dark:bg-gray-900'>
      {/* Header - Hide Back button when on dashboard */}
      {pathname !== ROUTES.DASHBOARD.ROOT && (
        <div className='px-6 py-4'>
          <Button
            variant='ghost'
            onClick={handleBack}
            className='flex h-10 w-[264px] flex-row items-center justify-center rounded bg-gray-50 p-2 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700'
            style={{
              width: '264px',
              height: '40px',
              padding: '8px',
              borderRadius: '4px',
            }}
          >
            <ChevronLeft className='mr-1 h-5 w-5' />
            <Typography size='p' weight='medium'>
              {t('dashboard.chat.back')}
            </Typography>
          </Button>
        </div>
      )}

      {/* Title */}
      <div className='px-6 py-4'>
        <div className='flex items-center'>
          <MessageCircle className='mr-3 h-6 w-6 text-gray-600 dark:text-gray-400' />
          <Typography
            size='p'
            weight='bold'
            className='text-gray-900 dark:text-gray-100'
          >
            {pathname === ROUTES.DASHBOARD.ROOT
              ? t('dashboard.chat.recentConversations')
              : t('dashboard.chat.myConversations')}
          </Typography>
        </div>
      </div>

      {/* Empty State or Conversations List */}
      <div className='flex-1 px-6 pb-4'>
        {isLoading ? (
          <div className='flex h-32 items-center justify-center'>
            <Typography size='p' color='secondary'>
              {t('dashboard.chat.loading')}
            </Typography>
          </div>
        ) : conversations.length === 0 ? (
          <div className='py-8 text-center'>
            <Typography size='p' color='secondary'>
              {t('dashboard.chat.noConversationsYet')}
            </Typography>
            <Typography size='span' color='secondary' className='mt-2'>
              {t('dashboard.chat.startConversation')}
            </Typography>
          </div>
        ) : (
          <div className='space-y-3'>
            {conversations
              .sort((a, b) => {
                // Sort by lastMessage createdAt (most recent first)
                const aDate = a.lastMessage?.createdAt
                  ? new Date(a.lastMessage.createdAt).getTime()
                  : new Date(a.updatedAt).getTime();
                const bDate = b.lastMessage?.createdAt
                  ? new Date(b.lastMessage.createdAt).getTime()
                  : new Date(b.updatedAt).getTime();
                return bDate - aDate;
              })
              .map((conversation) => {
                // Convert ConversationListItem to Conversation for helper function
                const conversationForHelper = {
                  ...conversation,
                  messages: conversation.lastMessage
                    ? [conversation.lastMessage]
                    : [],
                };
                const otherParticipant = getOtherParticipant(
                  conversationForHelper,
                  session?.user?.id || ''
                );
                const isSelected = selectedChatId === conversation.id;

                return (
                  <MemoizedConversationItem
                    key={conversation.id}
                    conversation={conversation}
                    isSelected={isSelected}
                    onClick={() => handleChatSelect(conversation.id)}
                    otherParticipant={otherParticipant}
                    formatTime={formatTime}
                    currentUserId={session?.user?.id || ''}
                    t={t}
                  />
                );
              })}

            {/* Search Button - Only show for non-lawyers */}
            {!isLawyer && (
              <div className='px-6 py-2'>
                <Button
                  variant='outline'
                  className='flex h-[35px] w-full flex-row items-center justify-center gap-1 rounded-lg border border-gray-700 p-2 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700'
                  style={{
                    height: '35px',
                    padding: '8px',
                    gap: '4px',
                    borderRadius: '8px',
                  }}
                  onClick={handleBack}
                >
                  <Search className='h-4 w-4' />
                  {t('dashboard.chat.searchForLawyers')}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

export default ChatSidebar;
